import type { NotificationChannel, NotificationKind, NotificationRecipient } from './types';

// Pure-function channel routing. Given an event and a recipient, returns
// the channels the notification should go out on.
//
// Two layers of filtering:
//   1. Recipient capability — no email address → no email; no phone → no SMS
//   2. Recipient preference — explicit row in notification_preferences wins,
//      otherwise the default below applies.
//
// The Edge Function `notify` consumes this; the UI uses it to preview what
// will actually fire ("you'll get this via email" affordance).

export function defaultPreference(kind: NotificationKind, channel: NotificationChannel): boolean {
  if (channel === 'in_app') return true;

  if (channel === 'email') {
    // Every kind opts into email by default — it's the cheapest channel
    // and the customer reads it later.
    return true;
  }

  if (channel === 'sms') {
    // Time-critical only. Twilio is ~$0.008/msg; per-family monthly cap
    // applies (see rateLimits.ts). Per plan §3.
    return kind === 'schedule_change' || kind === 'payment_failed';
  }

  if (channel === 'push') {
    // If the user subscribed, we send by default. The subscribe action
    // is itself an opt-in.
    return true;
  }

  return false;
}

export function isChannelAvailable(
  recipient: NotificationRecipient,
  channel: NotificationChannel,
  hasPushSubscription: boolean,
): boolean {
  switch (channel) {
    case 'email':
      return !!recipient.email;
    case 'sms':
      return !!recipient.phone;
    case 'push':
      return hasPushSubscription;
    case 'in_app':
      return true;
  }
}

// Resolve a single recipient × kind → list of channels to actually send on.
export function resolveChannelsForRecipient(
  recipient: NotificationRecipient,
  kind: NotificationKind,
  hasPushSubscription: boolean,
  requestedChannels: readonly NotificationChannel[] = ['email', 'sms', 'push', 'in_app'],
): NotificationChannel[] {
  const out: NotificationChannel[] = [];
  for (const channel of requestedChannels) {
    if (!isChannelAvailable(recipient, channel, hasPushSubscription)) continue;
    const prefKey = `${kind}:${channel}`;
    const explicit = recipient.preferences[prefKey];
    const enabled = explicit ?? defaultPreference(kind, channel);
    if (enabled) out.push(channel);
  }
  return out;
}

// Convenience: build the prefKey the Edge Function and UI both use to look
// up explicit preferences in the recipient.preferences map.
export function preferenceKey(kind: NotificationKind, channel: NotificationChannel): string {
  return `${kind}:${channel}`;
}
